import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaFacebook,
  FaWhatsapp,
  FaLinkedin,
  FaDiscord,
  FaTimes,
} from "react-icons/fa";
import { SiGmail } from "react-icons/si";
import { BsMicrosoftTeams } from "react-icons/bs";
import toast from "react-hot-toast";

interface QuickContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultPlatform?: string;
}

const platformData: Record<
  string,
  { icon: React.ReactNode; hint: string; placeholder: string }
> = {
  Gmail: {
    icon: <SiGmail />,
    hint: "What's your email address?",
    placeholder: "hello@example.com",
  },
  Facebook: {
    icon: <FaFacebook />,
    hint: "Drop your profile link",
    placeholder: "facebook.com/yourname",
  },
  "Microsoft Teams": {
    icon: <BsMicrosoftTeams />,
    hint: "What's your Teams email/link?",
    placeholder: "email@domain.com",
  },
  WhatsApp: {
    icon: <FaWhatsapp />,
    hint: "Enter your phone number with country code",
    placeholder: "+63 912 345 6789",
  },
  LinkedIn: {
    icon: <FaLinkedin />,
    hint: "Drop your LinkedIn profile URL",
    placeholder: "linkedin.com/in/yourname",
  },
  Discord: {
    icon: <FaDiscord />,
    hint: "What's your Discord username?",
    placeholder: "@username",
  },
};

const platforms = Object.keys(platformData);

const QuickContactModal: React.FC<QuickContactModalProps> = ({
  isOpen,
  onClose,
  defaultPlatform = "Gmail",
}) => {
  const [selectedPlatform, setSelectedPlatform] =
    useState<string>(defaultPlatform);
  const [contactHandle, setContactHandle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && defaultPlatform) {
      setSelectedPlatform(defaultPlatform);
      setContactHandle("");
    }
  }, [isOpen, defaultPlatform]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    // Spam Prevention: 60-second cooldown
    const lastSubmit = localStorage.getItem("portfolio_last_submit");
    if (lastSubmit && Date.now() - parseInt(lastSubmit) < 60000) {
      toast.error("Please wait a minute before submitting another request.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      formType: "Quick Contact",
      platform: selectedPlatform,
      contactHandle: contactHandle,
    };

    try {
      const SHEET_API_URL = process.env.BUN_PUBLIC_SHEET_API_URL;

      if (!SHEET_API_URL) {
        throw new Error("API URL is not configured.");
      }

      await fetch(SHEET_API_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "text/plain",
        },
        body: JSON.stringify(payload),
      });

      // Record submission time for spam prevention
      localStorage.setItem("portfolio_last_submit", Date.now().toString());

      toast.success("Inquiry sent! I'll be in touch within 24 hours. 🚀");
      setContactHandle("");
      onClose();
    } catch (error) {
      console.error("Failed to send inquiry:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const platformConfig = platformData[selectedPlatform];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-lg bg-base-100 border border-base-300 rounded-3xl shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-base-200">
          <h2 className="text-xl font-bold text-base-content">Quick Connect</h2>
          <button
            onClick={onClose}
            className="p-2 text-base-content/60 hover:text-error transition-colors rounded-full hover:bg-base-200"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-6">
          <p className="text-base-content/80 text-center">
            Interested in working together? Drop your handle and I'll reach out
            within 24 hours.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            {platforms.map((platform) => {
              const isSelected = selectedPlatform === platform;
              return (
                <button
                  key={platform}
                  onClick={() => setSelectedPlatform(platform)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all ${
                    isSelected
                      ? "border-primary bg-primary text-white shadow-md"
                      : "border-base-300 text-base-content/70 hover:bg-base-200"
                  }`}
                >
                  {platformData[platform]?.icon}
                  <span className="font-medium text-sm text-gray-200">
                    {platform}
                  </span>
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            {platformConfig && (
              <motion.div
                key={selectedPlatform}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-2"
              >
                <label className="text-sm font-medium text-base-content/80">
                  {platformConfig.hint}
                </label>
                <input
                  type="text"
                  value={contactHandle}
                  onChange={(e) => setContactHandle(e.target.value)}
                  placeholder={platformConfig.placeholder}
                  className="w-full px-4 py-3 bg-base-200 border border-base-300 rounded-xl focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-gray-100 placeholder:text-gray-400"
                />
                <button
                  onClick={handleSubmit}
                  disabled={!contactHandle.trim() || isSubmitting}
                  className="mt-4 w-full py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
                >
                  {isSubmitting ? "Sending..." : "Send Contact Info"}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default QuickContactModal;
